// Title, Consumed summary and the Memory Web HUD.
import { useEffect, useState } from 'react';
import { SPRITES_BY_ID } from '../../art/sprites';
import { NODES } from '../../content/registry';
import { TITLE_CODE } from '../../content/secrets';
import { unlocksOf } from '../../content/webLayout';
import { demoAllows, demoOver, STORE_URL } from '../../platform/demo';
import { t } from '../../i18n/t';
import { controller, getRenderer } from '../../render';
import { audio } from '../../audio/audio';
import { nodeStatus } from '../../sim/meta';
import { metaModifiers } from '../../sim/modifiers';
import type { SizeId } from '../../sim/types';
import { setUI, useGame, useUI } from '../../state/store';
import { NODE_LIST } from '../../content/registry';
import { Btn, Frame, Icon, KText, useFmt } from '../common';
import { CharmTip } from '../hud/RightPanel';
import { tip } from '../tooltip/Tooltip';
import { MoltButton } from './Reliquary';

function wakeUp(): void {
  audio.unlock();
  audio.music.start();
  const g = controller;
  setUI({ screen: 'machine', selectedNode: null });
  g.act({ t: 'NEW_RUN' });
}

export function Title() {
  const screen = useUI((u) => u.screen);
  const run = useGame((g) => g.run);
  const consumed = useGame((g) => g.meta.lifetime.consumed);
  const [chomp, setChomp] = useState(0);
  // Secret (docs/06 §8): typing FEED on the title makes the logo chomp.
  useEffect(() => {
    if (screen !== 'title') return;
    let typed = '';
    const onKey = (e: KeyboardEvent) => {
      typed = (typed + e.key.toLowerCase()).slice(-TITLE_CODE.length);
      if (typed === TITLE_CODE) {
        audio.unlock();
        audio.play('chomp');
        setChomp((n) => n + 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screen]);
  if (screen !== 'title') return null;
  const resume = () => {
    audio.unlock();
    audio.music.start();
    if (run && run.phase !== 'consumed') setUI({ screen: 'machine' });
    else if (consumed > 0) setUI({ screen: 'web' });
    else wakeUp();
  };
  return (
    <div className="overlay interactive" style={{ inset: 0, background: '#2e222f', flexDirection: 'column', gap: 10 }}>
      {SPRITES_BY_ID.has('keyart') && <Icon id="keyart" scale={4} style={{ position: 'absolute', left: 0, top: 0 }} />}
      {SPRITES_BY_ID.has('logo_maw') ? (
        <div key={chomp} className={`title-logo ${chomp ? 'logo-chomp' : ''}`} data-testid="logo" title={t('ui.title')}>
          <Icon id="logo_maw" scale={2} />
        </div>
      ) : (
        <div
          key={chomp}
          className={`title-font ${chomp ? 'logo-chomp' : ''}`}
          data-testid="logo"
          style={{ fontSize: 64, lineHeight: '56px', color: '#fbb954', textShadow: '4px 4px 0 #9e4539' }}
        >
          {t('ui.title')}
        </div>
      )}
      {SPRITES_BY_ID.has('logo_sub') ? (
        <div className="title-logo" title={t('ui.subtitle')}>
          <Icon id="logo_sub" scale={2} />
        </div>
      ) : (
        <div className="h" style={{ color: '#e83b3b', letterSpacing: 2 }}>
          {t('ui.subtitle')}
        </div>
      )}
      <div style={{ height: 20 }} />
      <Btn kind="primary" onClick={resume} testId="wake-up" style={{ position: 'relative', zIndex: 1, fontSize: 16, padding: '4px 14px 5px' }}>
        {run || consumed > 0 ? t('ui.continue') : t('ui.wakeUp')}
      </Btn>
    </div>
  );
}

/** The demo's end card (docs/09 Phase 6): once the cap is reached, point to the full game. */
function DemoEndCard() {
  const meta = useGame((g) => g.meta);
  if (!demoOver(meta)) return null;
  return (
    <Frame style={{ textAlign: 'center', width: 280 }}>
      <div className="h" style={{ color: '#fbb954' }}>
        {t('ui.demo.title')}
      </div>
      <div style={{ lineHeight: '11px' }}>{t('ui.demo.body')}</div>
      <div className="dim">{t('ui.demo.noMoney')}</div>
      {STORE_URL && (
        <Btn kind="gold" onClick={() => window.open(STORE_URL, '_blank', 'noopener')} testId="wishlist">
          {t('ui.demo.wishlist')}
        </Btn>
      )}
    </Frame>
  );
}

/** Mother's Locket: choose the charm that survives being eaten. */
function LocketChoice() {
  const run = useGame((g) => g.run);
  if (!run || run.flags.locketCharm === undefined || !run.charms.length) return null;
  return (
    <Frame style={{ textAlign: 'center' }}>
      <div className="h" style={{ color: '#eaaded' }}>
        {t('ui.locket.title')}
      </div>
      <div className="dim">{t('ui.locket.hint')}</div>
      <div className="row" style={{ justifyContent: 'center', gap: 4, marginTop: 3 }}>
        {run.charms.map((id, i) => (
          <div
            key={id}
            className={`card ${run.flags.locketCharm === i ? '' : 'pick'} interactive`}
            style={{ padding: 2, boxShadow: run.flags.locketCharm === i ? '0 0 0 2px #eaaded' : undefined }}
            onClick={() => controller.act({ t: 'KEEP_CHARM', index: i })}
            {...tip(<CharmTip id={id} />)}
          >
            <Icon id={`charm_${id}`} />
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function Consumed() {
  const stage = useUI((u) => u.stage);
  const screen = useUI((u) => u.screen);
  const meta = useGame((g) => g.meta);
  const endless = useGame((g) => !!g.run?.flags.endless);
  const fmt = useFmt();
  if (screen !== 'machine' || stage !== 'consumed' || !meta.lastRun) return null;
  const r = meta.lastRun;
  const affordable = NODE_LIST.filter((n) => nodeStatus(meta, n.id).status === 'affordable').slice(0, 3);
  return (
    <div className="overlay interactive" style={{ flexDirection: 'column', gap: 8 }}>
      <div className="h2" style={{ color: '#e83b3b' }}>
        {r.reason === 'ending' ? t(`ui.ending.name.${r.ending ?? 'sated'}`) : r.reason === 'offer' ? t('ui.consumed.offer') : t('ui.consumed.title')}
      </div>
      <div>{t('ui.consumed.reached', { n: r.n })}</div>
      <div>{t('ui.consumed.paid', { n: r.paid })}</div>
      <div>{t('ui.consumed.best', { coins: fmt(r.bestWin) })}</div>
      {r.reckonings ? <div className="kw-bad">{t('ui.consumed.reckonings', { n: r.reckonings })}</div> : null}
      {endless ? <div className="kw-bad">{t('ui.endless.best', { n: meta.lifetime.bestEndless })}</div> : null}
      <LocketChoice />
      <DemoEndCard />
      <div className="h" style={{ color: '#a884f3' }}>
        {t('ui.consumed.souls', { n: r.souls })}
      </div>
      {affordable.length > 0 && (
        <Frame style={{ textAlign: 'center' }}>
          <div className="dim">{t('ui.consumed.afford')}</div>
          {affordable.map((n) => (
            <div key={n.id} style={{ color: '#eaaded' }}>
              {t(`node.${n.id}.name`)}
            </div>
          ))}
        </Frame>
      )}
      <Btn kind="primary" onClick={() => setUI({ screen: 'web', stage: 'play' })} testId="to-web">
        {t('ui.consumed.toWeb')} ▶
      </Btn>
    </div>
  );
}

const SIZES: SizeId[] = ['bandit', 'cabinet', 'wide', 'grand', 'tall', 'cathedral'];

/** Play a smaller machine on purpose (docs/04 §1.1; needed for Minimalist). */
function SizePicker() {
  const meta = useGame((g) => g.meta);
  const grown = metaModifiers(meta).size;
  const max = SIZES.indexOf(grown);
  if (max <= 0) return null;
  const chosen = meta.runSize && SIZES.indexOf(meta.runSize) <= max ? meta.runSize : grown;
  return (
    <div className="row" style={{ gap: 2 }}>
      <span className="dim">{t('ui.size.title')}</span>
      {SIZES.slice(0, max + 1).map((sz) => (
        <Btn key={sz} kind={chosen === sz ? 'primary' : 'ghost'} onClick={() => controller.act({ t: 'SET_RUN_SIZE', size: sz === grown ? null : sz })}>
          {t(`ui.size.${sz}`)}
        </Btn>
      ))}
    </div>
  );
}

export function WebHUD() {
  const screen = useUI((u) => u.screen);
  const selected = useUI((u) => u.selectedNode);
  const meta = useGame((g) => g.meta);
  if (screen !== 'web') return null;
  const def = selected && demoAllows(selected) ? NODES.get(selected) : null;
  const st = selected ? nodeStatus(meta, selected) : null;
  const level = selected ? (meta.nodes[selected] ?? 0) : 0;
  return (
    <>
      <div style={{ position: 'absolute', left: 8, top: 22 }} className="col interactive">
        <span className="h" style={{ color: '#a884f3' }}>
          {t('ui.web.title')}
        </span>
        <span className="dim">{t('ui.web.hint')}</span>
        <MoltButton />
      </div>
      {def && st && st.status !== 'hidden' && (
        <Frame style={{ position: 'absolute', left: 8, bottom: 20, width: 220 }} className="interactive">
          <div className="col" style={{ gap: 4 }}>
            <div className="row">
              <span className="h" style={{ color: def.kind === 'stat' ? '#c7dcd0' : '#fbb954' }}>
                {t(`node.${def.id}.name`)}
              </span>
              <span className="dim">{def.maxLevel > 1 ? t('ui.web.level', { level, max: def.maxLevel }) : def.kind === 'stat' ? t('ui.web.stat') : t('ui.web.gate')}</span>
            </div>
            <KText text={t(`node.${def.id}.desc`)} />
            <div style={{ color: '#a884f3', fontStyle: 'normal' }}>{t(`node.${def.id}.flavor`)}</div>
            {def.kind !== 'stat' && unlocksOf(def.id).length > 0 && (
              <div className="dim" data-testid="node-unlocks">
                {t('ui.web.unlocks', { list: unlocksOf(def.id).map((id) => t(`node.${id}.name`)).join(', ') })}
              </div>
            )}
            {st.status === 'maxed' ? (
              <div className="dim">{t('ui.web.maxed')}</div>
            ) : st.status === 'locked' ? (
              <div className="kw-bad">
                {st.needTribute ? t('ui.web.needTribute', { n: st.needTribute }) : t('ui.web.locked', { req: st.missing.map((m) => t(`node.${m}.name`)).join(', ') })}
              </div>
            ) : (
              <Btn
                kind="primary"
                disabled={st.status !== 'affordable'}
                onClick={() => controller.act({ t: 'BUY_NODE', id: def.id })}
                testId="buy-node"
              >
                {t('ui.web.buy', { cost: st.cost })}
              </Btn>
            )}
          </div>
        </Frame>
      )}
      <div className="interactive col" style={{ position: 'absolute', right: 10, bottom: 20, alignItems: 'flex-end', gap: 6 }}>
        <SizePicker />
        <Btn kind="primary" onClick={wakeUp} testId="wake-up-web" style={{ fontSize: 16, padding: '4px 12px 5px' }}>
          {t('ui.wakeUp')}
        </Btn>
      </div>
      <div className="interactive" style={{ position: 'absolute', right: 10, top: 22 }}>
        <Btn kind="ghost" onClick={() => getRenderer()?.web.resetView()}>
          ◎
        </Btn>
      </div>
    </>
  );
}
